import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { useAuth, InteractionStatus, isAuthConfigured } from './auth';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { Button } from './components/common/Button';
import { AuthErrorBanner } from './components/common/AuthErrorBanner';

// Route-based code splitting: pages load only when their route is visited
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const NewRequestPage = lazy(() =>
  import('./pages/NewRequestPage').then((m) => ({ default: m.NewRequestPage }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const PermissionsPage = lazy(() =>
  import('./pages/PermissionsPage').then((m) => ({ default: m.PermissionsPage }))
);
const RequestDetailsPage = lazy(() =>
  import('./pages/RequestDetailsPage').then((m) => ({ default: m.RequestDetailsPage }))
);

function HeaderAuth() {
  const { user, interactionStatus, login, logout, error } = useAuth();
  const displayName = user?.name ?? user?.username ?? '';
  const loading = isAuthConfigured && interactionStatus !== InteractionStatus.None;

  if (loading) {
    return (
      <span className="text-sm text-text-tertiary" aria-live="polite">
        {interactionStatus === InteractionStatus.Logout ? 'Signing out…' : 'Signing in…'}
      </span>
    );
  }

  if (user) {
    return (
      <div className="flex items-center gap-4">
        <span
          className="text-sm text-text-secondary max-w-[200px] truncate"
          title={displayName}
        >
          {displayName}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={() => logout()}
          aria-label="Sign out"
        >
          Sign out
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {error && (
        <span className="text-sm text-error font-medium" aria-hidden="true">
          Sign in failed
        </span>
      )}
      <Button
        type="button"
        variant="primary"
        size="md"
        onClick={() => login()}
        aria-label="Sign in"
      >
        Sign in
      </Button>
    </div>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background-subtle">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-text focus:rounded-component focus:outline focus:outline-2 focus:outline-grey-100 focus:outline-offset-1"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-surface border-b border-border-DEFAULT">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <svg
                  className="h-8 w-8 text-primary"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                <span className="ml-2 text-xl font-bold text-text-primary">
                  EDIEL Access
                </span>
              </Link>
            </div>
            <nav className="flex items-center gap-4" aria-label="Main navigation">
              <Link
                to="/"
                className="text-sm font-medium text-text-secondary hover:text-text-primary"
              >
                Dashboard
              </Link>
              <Link
                to="/permissions"
                className="text-sm font-medium text-text-secondary hover:text-text-primary"
              >
                Permissions
              </Link>
              <Link
                to="/requests/new"
                className="text-sm font-medium text-text-secondary hover:text-text-primary"
              >
                New Request
              </Link>
              <HeaderAuth />
            </nav>
          </div>
        </div>
      </header>

      {/* Auth error banner */}
      <AuthErrorBanner />

      {/* Main content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" tabIndex={-1}>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-surface border-t border-border-DEFAULT mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-text-secondary">
            EDIEL Access Request Management System
          </p>
        </div>
      </footer>
    </div>
  );
}

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-16" aria-live="polite">
      <span className="text-text-secondary">Loading…</span>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/permissions" element={<ProtectedRoute><PermissionsPage /></ProtectedRoute>} />
            <Route path="/requests/new" element={<ProtectedRoute><NewRequestPage /></ProtectedRoute>} />
            <Route path="/requests/:id" element={<ProtectedRoute><RequestDetailsPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}
